import React, { PropsWithChildren } from "react";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./HprAndGemComparison.scss";
import CustomModal from "../../CustomModal/CustomModal";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import XIcon from "../../icons/XIcon";
import ImageHyperCoinAndGemStack from "../../../images/hyper-coin-gem-stack.svg";
import CheckmarkIcon from "../../icons/CheckmarkIcon";
import SolidMinusIcon from "../../icons/SolidMinusIcon";
import Gem from "../../Gem/Gem";
import HyperCoin from "../../HyperCoin/HyperCoin";
import { ExtraTag } from "../ExtraTag/ExtraTag";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

enum BundleChecklistItemTypes {
  ALLOWED,
  RESTRICTED,
}

interface BundleChecklistItemProps {
  type?: BundleChecklistItemTypes;
}

interface BundleChecklistSectionProps {
  title: string;
  description?: React.ReactNode;
}

function BundleChecklistItem({
  type = BundleChecklistItemTypes.ALLOWED,
  children,
}: PropsWithChildren<BundleChecklistItemProps>) {
  return (
    <Stack
      as="li"
      gap={2}
      direction="horizontal"
      className="BundleChecklistItem"
    >
      <div
        className={`IconContainer ${
          type === BundleChecklistItemTypes.ALLOWED ? "Allowed" : "Restricted"
        }`}
      >
        {type === BundleChecklistItemTypes.ALLOWED ? (
          <CheckmarkIcon />
        ) : (
          <SolidMinusIcon width={12} height={12} />
        )}
      </div>
      <p className="Grotesk Content">{children}</p>
    </Stack>
  );
}

function BundleChecklistSection({
  title,
  description,
  children,
}: PropsWithChildren<BundleChecklistSectionProps>) {
  return (
    <Stack className="BundleChecklistSection">
      <h4 className="text-uppercase Grotesk BundleTitle">{title}</h4>
      <Stack as="ul" gap={3} className="mb-0 BundleChecklistItems">
        {description && (
          <div className="Grotesk BundleDescription">{description}</div>
        )}
        {children}
      </Stack>
    </Stack>
  );
}

export default function HprAndGemComparison({
  isOpen,
  onClose,
}: GenericModalProps) {
  const { t } = useTranslation("games");
  const { isFiatDisplay } = useHprFiatFormatter();

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      isFullscreen
      showCloseBtn={false}
      customClass="HprAndGemComparisonModal"
    >
      <div className="Header">
        <button
          aria-label="Close modal"
          onClick={onClose}
          className="CloseButton"
        >
          <XIcon bgColor="#F5F5F5" strokeColor="#434343" />
        </button>
        <div className="text-center">
          <Image src={ImageHyperCoinAndGemStack} alt="Hyper coins and Gems" />
        </div>
      </div>

      <Stack gap={4} className="MainContent">
        <Stack className="HprAndGemDescription">
          <h3 className="Hanson-700 Title">
            {isFiatDisplay
              ? t("hpr_and_gem_comparison.title_fiat")
              : t("hpr_and_gem_comparison.title_hpr")}
          </h3>
          <p className="Grotesk Description">
            {isFiatDisplay
              ? t("hpr_and_gem_comparison.description_fiat")
              : t("hpr_and_gem_comparison.description_hpr")}
          </p>
        </Stack>

        <BundleChecklistSection title={t("hpr_and_gem_comparison.hpr_bundles")}>
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.play_battle_games")}
          </BundleChecklistItem>
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.play_challenge_games")}
          </BundleChecklistItem>
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.compete_in_tournament")}
          </BundleChecklistItem>
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.available_for_cashout")}
          </BundleChecklistItem>
        </BundleChecklistSection>

        <BundleChecklistSection
          title={t("hpr_and_gem_comparison.bonus_bundles")}
          description={
            <p>
              {isFiatDisplay
                ? t("hpr_and_gem_comparison.bonus_bundles_description_fiat")
                : t(
                    "hpr_and_gem_comparison.bonus_bundles_description_hpr"
                  )}{" "}
              <ExtraTag />{" "}
              {t("hpr_and_gem_comparison.bonus_bundles_description_end")}
            </p>
          }
        >
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.play_battle_games")}
          </BundleChecklistItem>
          <BundleChecklistItem>
            {t("hpr_and_gem_comparison.compete_in_tournament")}
          </BundleChecklistItem>
          {!isFiatDisplay && (
            <BundleChecklistItem>
              Convert{" "}
              <span className="d-inline-flex justify-content-center align-items-center CurrencyWithText">
                <Gem width={16} height={16} />
                GEM
              </span>{" "}
              to{" "}
              <span className="d-inline-flex justify-content-center align-items-center CurrencyWithText">
                <HyperCoin width={13.3} height={13.3} />
                HPR
              </span>
            </BundleChecklistItem>
          )}
          <BundleChecklistItem type={BundleChecklistItemTypes.RESTRICTED}>
            {t("hpr_and_gem_comparison.play_challenge_games")}
          </BundleChecklistItem>
          <BundleChecklistItem type={BundleChecklistItemTypes.RESTRICTED}>
            {t("hpr_and_gem_comparison.available_for_cashout")}
          </BundleChecklistItem>
        </BundleChecklistSection>
      </Stack>
    </CustomModal>
  );
}
